/**
 * TrivenyRoleChangeModal
 *
 * Multi-role assignment for a single Triveny tenant_user. Fetches:
 *   GET /api/platform/roles                — available role definitions
 *   GET /api/platform/roles/users/{uuid}   — user's current assignments
 *
 * Saves via:
 *   PUT /api/platform/roles/users/{uuid}   — replaces the user's role set
 *
 * The user_id passed in MUST be the platform_shared.tenant_users.id UUID,
 * not the OrganizationUser.id pseudo-int from the bridged /api/cbos
 * list endpoint. UserManagement passes the `tenant_user_id` field that
 * the BE bridge now exposes.
 */

import { useEffect, useMemo, useState } from 'react';
import { config } from '@/src/config';
import { tvTokens } from '@/src/platform/shared/components/triveny';
import { authFetch } from '@/lib/authFetch';
import { Shield, X, Save, AlertCircle, Lock } from 'lucide-react';

interface RoleDef {
  id: string;
  role_key: string;
  display_name: string;
  description?: string | null;
  access_level: number;
  is_system: boolean;
  is_active: boolean;
  capabilities: string[];
}

interface Props {
  tenantUserId: string; // UUID
  userName: string;
  userEmail?: string | null;
  onClose: () => void;
  onSaved?: () => void;
}

export default function TrivenyRoleChangeModal({
  tenantUserId,
  userName,
  userEmail,
  onClose,
  onSaved,
}: Props) {
  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [assigned, setAssigned] = useState<Set<string>>(new Set());
  const [primary, setPrimary] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const baseUrl = config.weaveinApi.baseUrl;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [allRes, userRes] = await Promise.all([
          authFetch(`${baseUrl}/api/platform/roles`),
          authFetch(`${baseUrl}/api/platform/roles/users/${tenantUserId}`),
        ]);
        if (!allRes.ok) throw new Error(`Roles: ${allRes.status}`);
        const allRoles = (await allRes.json()) as RoleDef[];
        const userRoles = userRes.ok ? ((await userRes.json()) as RoleDef[]) : [];
        setRoles(allRoles.filter((r) => r.is_active));
        const assignedIds = new Set(userRoles.map((r) => r.id));
        setAssigned(assignedIds);
        // First in the GET /users/{id} response is the primary (server orders by is_primary DESC)
        setPrimary(userRoles[0]?.id ?? (allRoles.find((r) => r.role_key === 'tenant_user')?.id ?? null));
      } catch (err: any) {
        setError(err?.message || 'Failed to load roles');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantUserId, baseUrl]);

  const toggleRole = (id: string) => {
    const next = new Set(assigned);
    if (next.has(id)) {
      next.delete(id);
      if (primary === id) {
        // Pick another as primary, or null if no roles left
        const remaining = Array.from(next);
        setPrimary(remaining[0] ?? null);
      }
    } else {
      next.add(id);
      if (!primary) setPrimary(id);
    }
    setAssigned(next);
  };

  const save = async () => {
    if (assigned.size === 0) {
      setError('Pick at least one role');
      return;
    }
    if (!primary || !assigned.has(primary)) {
      setError('Pick a primary role');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch(`${baseUrl}/api/platform/roles/users/${tenantUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role_definition_ids: Array.from(assigned),
          primary_role_definition_id: primary,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `Save failed (${res.status})`);
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sortedRoles = useMemo(
    () => [...roles].sort((a, b) => b.access_level - a.access_level),
    [roles],
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,23,28,0.45)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={config.isTriveny ? 'triveny-platform' : undefined}
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 560,
          maxHeight: '85vh',
          overflowY: 'auto',
          fontFamily: tvTokens.fontSans,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Shield size={20} style={{ color: tvTokens.ink }} />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: tvTokens.ink }}>
              Assign roles
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: tvTokens.ink500, padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>
        <p style={{ margin: '0 0 18px 0', fontSize: 13, color: tvTokens.ink700 }}>
          {userName}
          {userEmail && (
            <span style={{ color: tvTokens.ink500 }}> · {userEmail}</span>
          )}
        </p>

        {error && (
          <div
            style={{
              marginBottom: 14,
              padding: '8px 10px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              color: '#991b1b',
              fontSize: 12,
              display: 'flex',
              gap: 6,
              alignItems: 'flex-start',
            }}
          >
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: tvTokens.ink500, fontSize: 13 }}>
            Loading roles…
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sortedRoles.map((r) => {
              const isAssigned = assigned.has(r.id);
              const isPrimary = primary === r.id;
              return (
                <div
                  key={r.id}
                  style={{
                    padding: '10px 12px',
                    border: `1px solid ${isAssigned ? tvTokens.ink : tvTokens.border}`,
                    borderRadius: 8,
                    background: isAssigned ? '#fafafa' : '#fff',
                    transition: 'background 100ms',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isAssigned}
                      onChange={() => toggleRole(r.id)}
                      disabled={saving}
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, fontWeight: 500, color: tvTokens.ink }}>
                          {r.display_name}
                        </span>
                        {r.is_system && (
                          <span
                            title="Built-in system role"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                              padding: '1px 6px',
                              borderRadius: 999,
                              fontSize: 9,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              background: '#eef2ff',
                              color: '#3730a3',
                            }}
                          >
                            <Lock size={9} /> System
                          </span>
                        )}
                        <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, color: tvTokens.ink500 }}>
                          {r.role_key}
                        </span>
                      </div>
                      {r.description && (
                        <div style={{ fontSize: 12, color: tvTokens.ink700, marginTop: 2 }}>
                          {r.description}
                        </div>
                      )}
                      {isAssigned && (
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <input
                            type="radio"
                            name="primary"
                            checked={isPrimary}
                            onChange={() => setPrimary(r.id)}
                            disabled={saving}
                          />
                          <span style={{ color: tvTokens.ink700 }}>Primary role (the one in the JWT claim)</span>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              color: tvTokens.ink,
              border: `1px solid ${tvTokens.border}`,
              borderRadius: 8,
              fontSize: 13,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || loading || assigned.size === 0}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              background: tvTokens.ink,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              cursor: saving || assigned.size === 0 ? 'not-allowed' : 'pointer',
              opacity: saving || assigned.size === 0 ? 0.6 : 1,
            }}
          >
            <Save size={14} />
            {saving ? 'Saving…' : 'Save roles'}
          </button>
        </div>
      </div>
    </div>
  );
}
